/* LinksComponent.js */

"use strict";

const _ = require("lodash");
const Steve = require("@gluenetworks/steve_core").getInstance();
const DefaultColumnComponent = require("@gluenetworks/steve_ui_widgets").GridGl
    .DefaultColumnComponent;
const Renderer = require("./LinksRenderer");
const disabledStatus = ["DOWNLOADING", "UPLOADING"];
const Registrations = require("../../../../../../../../../RegistrationNamespaces");
const DeleteImageComponent = require("../../veils/deleteImage/DeleteImageComponent");
const EditImageComponent = require("../../veils/editImage/EditImageComponent");

class LinksComponent extends DefaultColumnComponent {
    constructor(entity, context) {
        super(entity);
        this._entity = entity;
        this._context = context;
        this._gameEditor = context.gameEditor;
        this._linkStatus = _.get(entity, "linkStatus", "");
        this._activeOrgId = this._gameEditor._activeOrgId;
        this._odmOsPlatform = this.gameEditor._activeView._odmOsPlatform;
        this._OdmOsLinkEntity = this._gameEditor._activeView._OdmOsLinkEntity;
        this._isDisabled = false;
        if (_.includes(disabledStatus, this._linkStatus)) {
            this._isDisabled = true;
        }
        this._OdmOsPlatformClass = Steve.di.getRegistered(
            "$teve.entities.OdmOsPlatform"
        )?.registeredObj;
        this._renderer = new Renderer(this);
        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        if (this._entity) {
            this._entity.on(
                "LinkStatusPropertyUpdated",
                this._onLinkStatusPropertyUpdated.bind(this)
            );
        }
    }

    _unregisterListeners() {
        if (this._entity) {
            this._entity.removeAllListeners("LinkStatusPropertyUpdated");
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get gameEditor() {
        return this._gameEditor;
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        let prom = this._renderer._shutdown();
        this._renderer = undefined;
        this._context = undefined;
        this._linkStatus = undefined;
        this._isDisabled = undefined;
        this._entity = undefined;
        return prom;
    }

    /******************************************/
    /* START PUBLIC API METHODS               */
    /******************************************/

    setValue() {}

    _onLinkStatusPropertyUpdated() {
        this._linkStatus = this._entity.linkStatus;
        if (_.includes(disabledStatus, this._linkStatus)) {
            this._isDisabled = true;
        } else {
            this._isDisabled = false;
        }
        this.emit("LinkStatusPropertyUpdated");
    }

    /******************************************/
    /* END PUBLIC API METHODS                 */
    /******************************************/

    uploadImage() {
        this._gameEditor._activeView._selectedLinkForUploadImage = this._entity;
        this._gameEditor.runAction(Registrations.ODM_OS_PLATFORM_EDITOR_ACTIONS_UPLOAD_IMAGE);
    }

    editImage() {
        const logger = this._gameEditor._logger;

        this._gameEditor._activeView._selectedLinkForEditImage = this._entity;
        return this.gameEditor._modalUserFeedback
            .hideDisplayed()
            .then(() => {
                return this.gameEditor._modalUserFeedback.displayCustom(
                    new EditImageComponent(this._entity, logger),
                    {dontHide: true}
                );
            })
            .then((result) => {
                if (result && result.saved) {
                    // Entity has already been updated via setters, just save it
                    return this._entity.update();
                }
            })
            .then(() => {
                // Refresh the grid row if update was successful
                if (this._entity) {
                    this.emit("EntityUpdated");
                }
            })
            .catch((err) => {
                if (err && !err.userRejected) {
                    logger.warn(err, "LinksComponent.editImage() error");
                }
            })
            .finally(() => {
                this.gameEditor._modalUserFeedback.hideDisplayed();
            });
    }

    deleteImage() {
        const platformName = this._odmOsPlatform.getName();
        const logger = this._gameEditor._logger;
        const modalUserFeedback = this.gameEditor._modalUserFeedback;
        const entityId = this._entity.getId();

        this._gameEditor._activeView._selectedLinkForDeleteImage = this._entity;
        return modalUserFeedback
            .hideDisplayed()
            .then(() => {
                return modalUserFeedback.displayCustom(
                    new DeleteImageComponent(this._entity, platformName, logger),
                    {
                        dontHide: true,
                    }
                );
            })
            .then((result) => {
                if (result && result.confirmed) {
                    // Hide the veil BEFORE deleting to prevent empty veil state
                    return modalUserFeedback.hideDisplayed().then(() => {
                        if (!result.checkboxValue) {
                            return this._odmOsPlatform.removeFromLinksRelationship(entityId);
                        }
                        return this._removeAllRelationships();
                    });
                }
                return Promise.resolve();
            })
            .then(() => {
                if (!_.isEmpty(this._odmOsPlatform.getPendingRelationships())) {
                    return this._odmOsPlatform.updateRelationships();
                }
                return Promise.resolve();
            })
            .then(() => {
                return this._OdmOsPlatformClass
                    .loadOdmOsPlatform(this._activeOrgId, this._odmOsPlatform.getId())
                    .then((platform) => {
                        const ignoreLinkIds = platform.getIgnoreLinkIds();
                        if (!_.isEmpty(ignoreLinkIds) && ignoreLinkIds.includes(entityId)) {
                            platform.setIgnoreLinkIds(
                                ignoreLinkIds.filter((id) => id !== entityId)
                            );
                            return platform.update();
                        }
                        return Promise.resolve();
                    });
            })
            .catch((err) => {
                if (err && !err.userRejected) {
                    logger.warn(err, "LinksComponent.deleteImage() error");
                }
            })
            .finally(() => {
                // Ensure cleanup even on cancel
                modalUserFeedback?.hideDisplayed();
            });
    }

    _removeAllRelationships() {
        const logger = this._gameEditor._logger;
        const entityId = this._entity.getId();

        return this._OdmOsLinkEntity
            .loadRelated(
                this._activeOrgId,
                "OdmOsLink",
                entityId,
                "links",
                "OdmOsPlatform",
                true,
                {},
                {id: true},
                {name: 1},
                undefined
            )
            .then((platforms) => {
                // Early return if no platforms found
                if (!platforms || platforms.length === 0) {
                    logger.info("No platforms found to remove relationships from");
                    return [];
                }

                // Remove all relationships from the platforms
                const removalPromises = platforms.map((platform) =>
                    platform.removeFromLinksRelationship(entityId)
                );

                return Promise.all(removalPromises);
            })
            .then((platforms) => {
                // Filter out failed removals (null values)
                const validPlatforms = platforms.filter((platform) => platform !== null);

                if (validPlatforms.length === 0) {
                    logger.warn("No platforms to update after relationship removal");
                    return;
                }

                if (this.gameEditor) {
                    this.gameEditor._modalUserFeedback.hideDisplayed();
                }

                // Update all platforms asynchronously
                const updatePromises = validPlatforms.map((platform) =>
                    platform.update().catch((err) => {
                        logger.warn(err, `Failed to update platform ${platform.getId()}`);
                    })
                );

                return Promise.all(updatePromises);
            })
            .catch((err) => {
                logger.error(err, "LinksComponent._removeAllRelationships() error");
                // Ensure modal is hidden even on error
                if (this.gameEditor && this.gameEditor._modalUserFeedback) {
                    this.gameEditor._modalUserFeedback.hideDisplayed();
                }
                throw err; // Re-throw to propagate error
            });
    }
}

module.exports = LinksComponent;
